import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pickupToken, qrSecondsLeft, qrStep } from '@tikkas/shared';
import { IC, Svg } from '../art/icons.js';
import { qrSvg } from '../lib/qr.js';
import { useStore } from '../lib/store.js';

/** The rotating till code (docs/23 §3.1): a new token every step, the seconds left for the label. */
export function useRotatingToken(off: { customerId: string; qrSecret: string } | null): { token: string; sec: number } {
  const [token, setToken] = useState('');
  const [sec, setSec] = useState(qrSecondsLeft());
  useEffect(() => {
    if (!off) return;
    let live = true;
    let step = qrStep();
    pickupToken(off.qrSecret, off.customerId, new Date()).then((t) => live && setToken(t));
    const t = setInterval(() => {
      setSec(qrSecondsLeft());
      const st = qrStep();
      if (st !== step) {
        step = st;
        pickupToken(off.qrSecret, off.customerId, new Date()).then((tk) => live && setToken(tk));
      }
    }, 1000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [off?.customerId, off?.qrSecret]); // eslint-disable-line react-hooks/exhaustive-deps
  return { token, sec };
}

/**
 * The code for the till (CLAUDE.md §4, Saif 21 Oct): a centred white pop-up with the large QR, the name and "Show this at
 * the till", over a dimmed, blurred backdrop; while an order is on the go, its backup code sits under the QR (the line
 * left Home for here). Tap outside, swipe down or Escape closes it; a small close button too.
 */
export function CodeSheet({ onClose }: { onClose: () => void }) {
  const s = useStore();
  const { token } = useRotatingToken(s.offline);
  const name = s.offline?.name || s.me?.customer.firstName || 'Rewards Club member';
  const live = s.me?.liveOrders[0];
  const touch = useRef<{ y: number; t: number } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="qrsheet" onClick={onClose}>
      <div
        className="qrpop"
        role="dialog"
        aria-label="Your code for the till"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { const t = e.touches[0]; touch.current = t ? { y: t.clientY, t: Date.now() } : null; }}
        onTouchEnd={(e) => { const st = touch.current, t = e.changedTouches[0]; touch.current = null; if (st && t && t.clientY - st.y > 70 && Date.now() - st.t < 600) onClose(); }}
      >
        <button type="button" className="qrsheet-close" aria-label="Close" onClick={onClose}><Svg html={IC.close} /></button>
        <div className="qrsheet-qr" role="img" aria-label="Your rotating code for the till">{token ? <Svg html={qrSvg(token)} /> : null}</div>
        <b className="qrsheet-name">{name}</b>
        <span className="qrsheet-sub">Show this at the till</span>
        {live ? <span className="qrsheet-backup">Collecting order #{live.number}? Backup code <b>{live.backupCode}</b></span> : null}
      </div>
    </div>,
    document.body,
  );
}
